import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Search } from "lucide-react";
import { Input } from "@/components/ui/input";
import { PackCard } from "@/components/PackCard";
import { PackPreviewDialog } from "@/components/PackPreviewDialog";
import { PackRequestDialog } from "@/components/PackRequests";
import { SiteNav, SiteFooter, CWS_URL } from "@/components/SiteChrome";
import { AddConfirmBar } from "@/components/AddConfirmBar";
import { api } from "@/lib/api";

const STEPS = [
  ["1", "Install GlossLink", "Free on the Chrome Web Store. Takes ten seconds."],
  ["2", "Pick your textbook", "Preview the source and license, then click Add to my GlossLink."],
  ["3", "Read anywhere", "Every glossary term lights up on Canvas, PDFs, articles — hover to see the textbook's exact definition."],
];

export default function PacksPage() {
  const { packId } = useParams();
  const navigate = useNavigate();
  const [packs, setPacks] = useState(null);
  const [q, setQ] = useState("");
  const [subject, setSubject] = useState("All");
  const [requesting, setRequesting] = useState(false);

  const load = useCallback(() => api.get("/packs").then((r) => setPacks(r.data)), []);
  useEffect(() => { load(); }, [load]);
  const closePreview = useCallback((open) => { if (!open) navigate("/packs"); }, [navigate]);

  const subjects = useMemo(() => ["All", ...new Set((packs || []).map((p) => p.subject))], [packs]);
  const shown = (packs || []).filter((p) => (subject === "All" || p.subject === subject) &&
    `${p.title} ${p.textbook_title} ${p.authors}`.toLowerCase().includes(q.toLowerCase()));

  return (
    <div className="min-h-screen bg-slate-50 text-slate-900">
      <AddConfirmBar />
      <SiteNav />
      <main className="max-w-6xl mx-auto px-6 pt-14 pb-10">
        <header className="gl-fade-up text-center max-w-3xl mx-auto">
          <div className="inline-block bg-blue-100 text-blue-800 text-xs font-semibold px-3 py-1 rounded-full mb-5">Gloss Packs · Included with GlossLink</div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-tight">Your textbook's glossary, <span className="text-blue-600">one click away</span></h1>
          <p className="mt-5 text-lg text-slate-600">Complete glossaries copied word for word from open textbooks. Nothing to type, nothing to upload — add a pack and every term lights up as you read.</p>
        </header>

        <div className="mt-10 grid gap-4 sm:grid-cols-3" data-testid="packs-how-it-works">
          {STEPS.map(([n, title, body]) => (
            <div key={n} className="rounded-2xl border border-slate-200 bg-white p-5">
              <div className="h-8 w-8 rounded-full bg-blue-600 text-white font-bold grid place-items-center text-sm">{n}</div>
              <div className="mt-3 font-bold">{title}</div>
              <p className="mt-1 text-sm text-slate-600">{body}</p>
            </div>
          ))}
        </div>

        <div className="mt-14 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex flex-wrap gap-2">
            {subjects.map((s) => (
              <button key={s} data-testid={`packs-subject-filter-${s.toLowerCase().replace(/\s+/g, "-")}`} onClick={() => setSubject(s)}
                className={`rounded-full border px-3.5 py-1 text-sm transition-colors ${subject === s ? "border-blue-600 bg-blue-600 text-white" : "border-slate-300 bg-white text-slate-700 hover:border-slate-500"}`}>{s}</button>
            ))}
          </div>
          <div className="relative sm:w-80">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <Input data-testid="packs-search-input" placeholder="Search textbooks, authors" value={q} onChange={(e) => setQ(e.target.value)} className="h-11 rounded-xl bg-white pl-9" />
          </div>
        </div>

        {packs === null ? <p className="mt-10 text-slate-500">Loading packs…</p> : shown.length === 0 ? (
          <p data-testid="packs-empty" className="mt-8 rounded-2xl border border-dashed border-slate-300 bg-white p-10 text-center text-slate-500">
            No packs match your search. <button className="font-semibold text-blue-700 hover:underline" onClick={() => setRequesting(true)} data-testid="packs-empty-request-link">Request this textbook</button>
          </p>
        ) : (
          <div className="mt-8 grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3" data-testid="packs-grid">
            {shown.map((p, i) => <PackCard key={p.id} pack={p} index={i} onPreview={(id) => navigate(`/packs/${id}`)} />)}
          </div>
        )}

        <section className="mt-16 rounded-3xl bg-white border border-slate-200 p-8 sm:p-10 text-center" data-testid="packs-request-section">
          <h2 className="text-2xl font-extrabold tracking-tight">Don't see your textbook?</h2>
          <p className="mt-2 text-slate-600 max-w-xl mx-auto">Tell us which one and we'll add it from the official glossary — one textbook at a time, every definition word for word.</p>
          <button onClick={() => setRequesting(true)} data-testid="packs-request-button" className="mt-6 inline-block rounded-xl border-2 border-blue-600 px-6 py-3 font-bold text-blue-700 hover:bg-blue-50">Request a textbook</button>
        </section>

        <p className="mt-10 text-center text-xs text-slate-400">Desktop Chrome only · Packs load into the <a href={CWS_URL} target="_blank" rel="noopener noreferrer" className="underline">GlossLink extension</a> · 20-day free trial of everything · then 20 most-used terms free forever · Pro ($9.99/mo or $75/yr) unlocks every pack</p>
      </main>
      <SiteFooter />
      <PackRequestDialog open={requesting} onOpenChange={setRequesting} />
      <PackPreviewDialog packId={packId} onOpenChange={closePreview} />
    </div>
  );
}
